import type { Ref } from 'react';

import { CalendarIcon, ChevronsUpDownIcon } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  getGroupByOptionsForMetric,
  getMetricById,
} from '@/modules/analytics/constants/metricsCatalog';
import { formatRangeLabel } from '@/modules/analytics/utils/formatRangeLabel';
import { type MetricDraft, getMetricTitle } from '@/modules/analytics/utils/metricDraft';

export const MetricDraftPreview = ({ ref }: { ref?: Ref<HTMLDivElement> }) => {
  const { t } = useTranslation();
  const { control } = useFormContext<MetricDraft>();
  const [metricId, groupBy, range] = useWatch({ control, name: ['metricId', 'groupBy', 'range'] });

  const metric = getMetricById(metricId);
  const groupLabels = getGroupByOptionsForMetric(metricId)
    .filter((opt) => groupBy.includes(opt.column))
    .map((opt) => opt.label);
  const columns =
    metric && groupLabels.length > 0 ? [...groupLabels, metric.valueLabel ?? metric.label] : [];

  return (
    <div
      ref={ref}
      role="region"
      aria-label={t('analytics.reports_detail_preview')}
      className="flex w-full flex-col gap-6 rounded-lg border border-stroke bg-background p-6"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <p className="text-base leading-6 font-semibold text-foreground">
            {getMetricTitle(metricId) ?? t('analytics.reports_detail_preview_select_metric')}
          </p>
          <p className="text-sm leading-5 text-foreground-muted">
            {groupLabels.length > 0
              ? t('analytics.reports_detail_group_by_summary', { 0: groupLabels.join(', ') })
              : t('analytics.reports_detail_preview_grouped_by')}
          </p>
        </div>
        {range && (
          <Badge
            type="outline"
            flavour="neutral"
            rounded={false}
            size="xs"
            leadingIcon={<CalendarIcon />}
            className="shrink-0 font-semibold"
          >
            {formatRangeLabel(range, t)}
          </Badge>
        )}
      </div>
      <Skeleton className="aspect-5/2 w-full animate-none rounded-lg" />
      <div className="flex flex-col gap-1.5">
        <div className="flex h-9 items-center rounded-md border border-stroke px-3">
          {columns.map((label, i) => (
            <span
              key={label}
              className={cn(
                'flex flex-1 items-center gap-1 text-sm leading-5 font-medium text-foreground-muted',
                i === columns.length - 1 && 'flex-none'
              )}
            >
              {label}
              <ChevronsUpDownIcon className="size-3.5" />
            </span>
          ))}
        </div>
        <Skeleton className="h-9 w-full animate-none rounded-md" />
        <Skeleton className="h-9 w-full animate-none rounded-md" />
      </div>
    </div>
  );
};
